import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, computed, inject, signal } from '@angular/core';
import { ProjectCorpusService } from '../../../../project-corpus/domain/project-corpus.service';
import { FailedUpload, ImportJob, ProjectPaper } from '../../../../project-corpus/domain/types';
import { missingMetadataFields } from '../../../../project-corpus/domain/paper-status';
import { BaseModalComponent } from '../../../modals/base-modal/base-modal.component';

@Component({
  selector: 'app-job-detail-dialog',
  standalone: true,
  imports: [CommonModule, BaseModalComponent],
  templateUrl: './job-detail-dialog.component.html',
})
export class JobDetailDialogComponent {
  private readonly service = inject(ProjectCorpusService);

  private readonly _job = signal<ImportJob | null>(null);
  @Input() set job(value: ImportJob | null) {
    this._job.set(value);
    if (value) this.load(value.id);
  }

  @Output() closed = new EventEmitter<void>();
  @Output() editPaper = new EventEmitter<ProjectPaper>();

  readonly papers = signal<ProjectPaper[]>([]);
  readonly failedUploads = signal<FailedUpload[]>([]);
  readonly currentJob = this._job.asReadonly();
  readonly isOpen = computed(() => this._job() !== null);
  readonly readyCount = computed(() => this.papers().filter((p) => p.status === 'COMPLETE').length);

  missing(p: ProjectPaper): string {
    const f = missingMetadataFields({
      title: p.title,
      abstractText: p.abstractText,
      authors: p.authors,
    });
    return f.length ? `Missing: ${f.join(', ')}` : '';
  }

  private load(jobId: string): void {
    this.service.listPapersForJob(jobId).subscribe((page) => this.papers.set([...page.items]));
    this.service.listFailedUploads(jobId).subscribe((f) => this.failedUploads.set(f));
  }

  onRetry(p: ProjectPaper): void {
    if (!p.pdfId) return;
    this.service.retryExtraction(p.pdfId).subscribe(() => {
      const job = this._job();
      if (job) this.load(job.id);
    });
  }

  onEdit(p: ProjectPaper): void {
    this.editPaper.emit(p);
  }

  onDismiss(f: FailedUpload): void {
    const job = this._job();
    if (!job) return;
    this.service
      .deleteFailedUpload(job.id, f.id)
      .subscribe(() => this.failedUploads.set(this.failedUploads().filter((x) => x.id !== f.id)));
  }

  /** Re-uploads the chosen PDF (new small import job), then clears the failure record. */
  onReupload(f: FailedUpload, input: HTMLInputElement): void {
    const file = input.files && input.files[0];
    if (!file) return;
    const job = this._job();
    this.service.createImportJob({ source: 'PDF', files: [file] }).subscribe(() => {
      if (job) {
        this.service
          .deleteFailedUpload(job.id, f.id)
          .subscribe(() =>
            this.failedUploads.set(this.failedUploads().filter((x) => x.id !== f.id)),
          );
      }
    });
    input.value = '';
  }

  onClose(): void {
    this._job.set(null);
    this.closed.emit();
  }
}
